import type { ProviderKind } from "@cmmd-forge/contracts";

const PUBLIC_PROVIDER_NAMES: Partial<Record<ProviderKind, string>> = {
  apex: "CMMD",
};

const PUBLIC_PROVIDER_LEAK_PATTERNS = [
  /\bDeepSeek\b/i,
  /\bKimi\b/i,
  /\bMoonshot\b/i,
  /\bdeepseek[-._a-z0-9]*\b/i,
  /\bkimi[-._a-z0-9]*\b/i,
  /\bmoonshot[-._a-z0-9]*\b/i,
  /\bAPEX_API_KEY\b/,
  /\bDEEPSEEK_API_KEY\b/,
  /\bKIMI_API_KEY\b/,
  /\bOPENAI_API_KEY\b/,
  /\bANTHROPIC_API_KEY\b/,
  /\bCURSOR_API_KEY\b/,
  /\bOPENCODE_API_KEY\b/,
  /\bforge_proxy_[A-Za-z0-9._-]+\b/,
  /\bProviderValidationError\b/,
  /\/forge-server\/node_modules\//,
  /https?:\/\/[^\s'"<>)]*\/api\/provider-egress\/[^\s'"<>)]*/i,
  /https?:\/\/[^\s'"<>)]*(?:apex|deepseek|moonshot|kimi)[^\s'"<>)]*/i,
] as const;

export const DEFAULT_PUBLIC_PROVIDER_ERROR =
  "CMMD is temporarily unavailable. Check the workspace AI connection and retry.";

/**
 * The sentence a Workspace-wallet Claude turn fails with on a CMMD runtime when
 * CMMD will not mint its egress token (CMMD#5917). The Claude adapter opens its
 * refusal with it, and `categorizePublicProviderError` returns it unchanged, so
 * the refusal still reads as a refusal when a production stack forces the
 * failure detail through sanitization. Anything else would read as an outage
 * and invite a retry on the personal login.
 */
export const CLAUDE_WORKSPACE_EGRESS_REFUSAL =
  "Claude could not get a Workspace egress token from CMMD for this turn. Refusing rather than falling back to the personal login.";
const CLAUDE_WORKSPACE_EGRESS_REFUSAL_LOWER = CLAUDE_WORKSPACE_EGRESS_REFUSAL.toLowerCase();

export function isProviderRateOrQuotaError(message: string | null | undefined): boolean {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return (
    /\b(rate[\s-]?limit|too\s+many\s+requests|usage[ _-]limit|usage_limit_reached|quota|insufficient_quota)\b/.test(
      normalized,
    ) || /\b429\b/.test(normalized)
  );
}

function providerLifecycleOrAccountCopy(message: string): string | null {
  if (
    /\bbound to provider account\b/.test(message) &&
    /\bcannot use provider account\b/.test(message)
  ) {
    return "Forge could not switch this conversation to the selected AI account. Retry the account switch.";
  }
  if (
    /\bforge_runtime_repairing\b/.test(message) ||
    /\bthis turn was interrupted because the environment is being repaired\b/.test(message)
  ) {
    return "This turn was interrupted because the environment is being repaired. Retry after the repair finishes.";
  }
  if (
    /\bforge_runtime_stopped\b/.test(message) ||
    /\bthis turn was interrupted because the environment was stopped\b/.test(message)
  ) {
    return "This turn was interrupted because the environment was stopped. Start the environment and retry.";
  }
  return null;
}

/**
 * Infer a SAFE, clearer failure category from a (possibly leaky) provider error.
 *
 * Returns a canned, user-facing message — it never echoes the raw input, so it
 * is safe to use even when the source message contains provider internals.
 * Used to give actionable feedback ("model isn't available", "rate-limited",
 * "auth failed") instead of the blanket "CMMD is temporarily unavailable."
 * Returns null when no category can be confidently inferred.
 */
export function categorizePublicProviderError(message: string | null | undefined): string | null {
  if (!message) return null;
  const m = message.toLowerCase();
  // First: the refusal can carry a bare "HTTP 403" from CMMD, which the auth
  // branch below would otherwise claim and turn into a reconnect prompt.
  if (m.includes(CLAUDE_WORKSPACE_EGRESS_REFUSAL_LOWER)) return CLAUDE_WORKSPACE_EGRESS_REFUSAL;
  const lifecycleOrAccountCopy = providerLifecycleOrAccountCopy(m);
  if (lifecycleOrAccountCopy !== null) return lifecycleOrAccountCopy;
  // FOR-36501, and first: the observed refusal carries "401 Unauthorized" and
  // the word "model", so both the model branch and the auth branch below can
  // claim it. The auth branch used to, and it sent the user to reconnect a
  // workspace whose auth was fine. The credential simply did not cover the
  // model the turn asked for, which is a different problem with a different fix.
  if (
    /\bforge_provider_model_not_allowed\b/.test(m) ||
    /\bnot\s+scoped\s+to\s+the\s+requested\s+model\b/.test(m)
  ) {
    return "The workspace AI credential does not cover the model this turn asked for. Retry, or pick that model for the thread.";
  }
  if (
    /\bmodel\b[\s\S]*\b(not\s+(found|available|supported)|does\s+not\s+exist|unsupported|invalid|unknown)\b/.test(
      m,
    ) ||
    /\b(unsupported|unknown|invalid)\b[\s\S]*\bmodel\b/.test(m) ||
    /\b(model_not_found|invalid_model|model_not_supported)\b/.test(m)
  ) {
    return "The selected model isn't available right now. Pick a different model and retry.";
  }
  if (isProviderRateOrQuotaError(m)) {
    return "The AI service is rate-limited or out of quota. Wait a moment and retry.";
  }
  // CMMD-Center/forge#3558 review item 6: CMMD's provider egress refuses a
  // dead or unrefreshable provider credential with a
  // `*_CREDENTIAL_RECONNECT_REQUIRED` code (CODEX_CREDENTIAL_RECONNECT_REQUIRED,
  // CLAUDE_CREDENTIAL_RECONNECT_REQUIRED) on a status that is neither 401 nor
  // 403, so it falls through every branch below to the generic fallback and
  // loses the one actionable fact the user needs. Checked before the auth
  // branch so this more specific code wins when both would otherwise match.
  if (/_credential_reconnect_required\b/.test(m)) {
    return "The workspace AI credential needs to be reconnected. Reconnect it in Settings > Providers, then retry.";
  }
  if (
    /\b(unauthorized|forbidden|authentication\s+(failed|error|required)|invalid\s+api\s+key|expired\s+(token|credential))\b/.test(
      m,
    ) ||
    /\b40[13]\b/.test(m)
  ) {
    return "Workspace AI authentication failed. Reconnect the workspace and retry.";
  }
  if (
    /\b(timed?\s*out|timeout|overloaded|service\s+unavailable)\b/.test(m) ||
    /\b50[234]\b/.test(m)
  ) {
    return "The AI service is temporarily overloaded. Retry in a moment.";
  }
  return null;
}

export function publicProviderDisplayName(provider: ProviderKind): string {
  return PUBLIC_PROVIDER_NAMES[provider] ?? provider;
}

export function containsPublicProviderLeak(message: string | null | undefined): boolean {
  if (!message) return false;
  return PUBLIC_PROVIDER_LEAK_PATTERNS.some((pattern) => pattern.test(message));
}

export function sanitizePublicProviderText(
  message: string | null | undefined,
  options: { readonly fallback?: string } = {},
): string | null {
  if (message === undefined || message === null) return null;
  const trimmed = message.trim();
  if (trimmed.length === 0) return null;
  if (!containsPublicProviderLeak(trimmed)) return trimmed;
  // The raw message leaks provider internals — never surface it. Prefer a
  // clearer category-based message (model/rate-limit/auth/overload) when we can
  // safely infer the failure type; otherwise fall back to the generic copy.
  return (
    categorizePublicProviderError(trimmed) ?? options.fallback ?? DEFAULT_PUBLIC_PROVIDER_ERROR
  );
}
